import { Component } from '@theme/component';

/**
 * A slide-in "Shop the Look" panel.
 *
 * Lists every product visible in an ambiance image. Opening is a plain
 * fixed-position panel (not a modal <dialog>) so the theme's global quick-add
 * modal can layer cleanly on top of it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [panel] - The sliding panel.
 *
 * @extends Component<Refs>
 */
export default class ShopTheLook extends Component {
  /**
   * Opens the panel and locks page scroll.
   */
  open() {
    if (this.hasAttribute('open')) return;

    this.setAttribute('open', '');
    document.documentElement.setAttribute('scroll-lock', '');
    document.addEventListener('keydown', this.#handleKeyDown);

    // Move focus into the panel for keyboard/screen-reader users.
    requestAnimationFrame(() => this.refs.panel?.focus());
  }

  /**
   * Closes the panel and restores page scroll.
   */
  close = () => {
    if (!this.hasAttribute('open')) return;

    this.removeAttribute('open');
    document.documentElement.removeAttribute('scroll-lock');
    document.removeEventListener('keydown', this.#handleKeyDown);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleKeyDown);
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * Closes the panel on Escape.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  };
}

if (!customElements.get('aw-shop-the-look')) {
  customElements.define('aw-shop-the-look', ShopTheLook);
}
